import * as React from 'react';
import { AccentDialog } from '../../dialogs/AccentDialog';
import { AccentButton } from '../AccentButton';
import { showDialog, t } from '../../services/HelperService';

const safeString = (v) => (v ?? '').toString();
const MAX_ROWS = 200;

/**
 * Opens a searchable picker over one template-catalog group (see AccentTemplateInputUnbound): the right-click
 * menu offers it in place of a flyout when a group marked `picker: true` has more items than its threshold.
 * Resolves the chosen item ({ label, token, snippet, onInsert, ... }) or null when cancelled.
 */
export const pickTemplateItem = ({ title, items }) => showDialog(<TemplateItemPickerDlg title={title} items={items} />)
  .then((r) => (r?.canceled ? null : (r?.item ?? null)));

const matches = (item, needle) => {
  if (!needle) return true;
  const hay = `${safeString(item?.search)} ${safeString(item?.label)} ${safeString(item?.token)}`.toLowerCase();
  return needle.split(/\s+/).filter(Boolean).every((word) => hay.includes(word));
};

const TemplateItemPickerDlg = ({ title, items, onClose }) => {
  const [search, setSearch] = React.useState('');
  const [highlight, setHighlight] = React.useState(0);
  const inputRef = React.useRef(null);
  const listRef = React.useRef(null);

  const all = React.useMemo(() => (Array.isArray(items) ? items : []).filter((it) => safeString(it?.token).trim()), [items]);
  const needle = search.trim().toLowerCase();
  const filtered = React.useMemo(() => all.filter((it) => matches(it, needle)), [all, needle]);
  const shown = filtered.length > MAX_ROWS ? filtered.slice(0, MAX_ROWS) : filtered;
  const hidden = filtered.length - shown.length;

  React.useEffect(() => { inputRef.current?.focus?.(); }, []);
  React.useEffect(() => { setHighlight(0); }, [needle]);
  React.useEffect(() => {
    const row = listRef.current?.querySelector?.(`[data-index="${highlight}"]`);
    row?.scrollIntoView?.({ block: 'nearest' });
  }, [highlight]);

  const pick = (item) => { if (item) onClose?.({ item }); };

  const onKeyDown = (e) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setHighlight((h) => Math.min(shown.length - 1, h + 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setHighlight((h) => Math.max(0, h - 1)); }
    else if (e.key === 'Enter') { e.preventDefault(); pick(shown[highlight]); }
  };

  return (
    <AccentDialog
      headerContent={title || t('Select')}
      onClose={onClose}
      relativeWidth={() => 640}
      relativeHeight={(d) => Math.min(620, d.height - 80)}
      actionsContent={[
        <AccentButton key="insert" tagName="insert" disabled={!shown[highlight]} onClick={(e) => { pick(shown[highlight]); e.promise(); }}>{t('Insert')}</AccentButton>,
        <AccentButton key="cancel" tagName="cancel" onClick={(e) => { onClose?.({ canceled: true }); e.promise(); }}>{t('Cancel')}</AccentButton>,
      ]}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, height: '100%', minHeight: 0 }} data-tagname="templateItemPicker">
        <input
          ref={inputRef}
          className="form-control form-control-sm"
          placeholder={t('Search...')}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onKeyDown={onKeyDown}
          data-tagname="templateItemPickerSearch"
        />
        <div className="small text-muted">
          {filtered.length === all.length
            ? t('{{count}} item(s)', { count: all.length })
            : t('{{count}} of {{total}} item(s) match', { count: filtered.length, total: all.length })}
        </div>
        <div ref={listRef} style={{ flex: 1, minHeight: 0, overflow: 'auto', border: '1px solid #e2e8f0', borderRadius: 4 }}>
          {shown.length === 0 && <div className="small text-muted" style={{ padding: 12 }}>{t('Nothing matches.')}</div>}
          {shown.map((it, i) => {
            const label = safeString(it.label).trim() || safeString(it.token);
            const selected = i === highlight;
            return (
              <div
                key={`${it.token}_${i}`}
                data-index={i}
                title={safeString(it.search) || label}
                onMouseEnter={() => setHighlight(i)}
                onClick={() => setHighlight(i)}
                onDoubleClick={() => pick(it)}
                style={{
                  padding: '4px 8px', fontSize: 12, cursor: 'pointer', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                  background: selected ? '#ede9fe' : 'transparent', color: selected ? '#4c1d95' : '#1f2937',
                }}
              >
                {label}
              </div>
            );
          })}
          {hidden > 0 && (
            <div className="small text-muted" style={{ padding: '6px 8px', borderTop: '1px solid #e2e8f0' }}>
              {t('{{count}} more - refine the search to see them.', { count: hidden })}
            </div>
          )}
        </div>
      </div>
    </AccentDialog>
  );
};
